import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Home, Building2, Sparkles, Key, Sofa, Droplets, Star, Quote } from 'lucide-react';
import { Hero } from '../components/Hero';
import { Stats } from '../components/Stats';
import { CtaBanner } from '../components/CtaBanner';
import { CLEANING_IMAGES } from '../constants/images';
const previewServices = [{
  icon: Home,
  title: 'House Cleaning',
  desc: 'Recurring or one-time for spotless homes.'
}, {
  icon: Building2,
  title: 'Office Cleaning',
  desc: 'Healthy workspaces that boost productivity.'
}, {
  icon: Sparkles,
  title: 'Deep Cleaning',
  desc: 'Top-to-bottom thorough refresh.'
}, {
  icon: Key,
  title: 'Move-In / Out',
  desc: 'Move with confidence into clean spaces.'
}, {
  icon: Sofa,
  title: 'Carpet Care',
  desc: 'Stain removal and fabric revival.'
}, {
  icon: Droplets,
  title: 'Windows',
  desc: 'Crystal-clear, streak-free views.'
}];
const homeTestimonials = [{
  name: 'Sarah Jenkins',
  role: 'Homeowner • Clifton',
  img: 'https://i.pravatar.cc/150?img=44',
  quote: 'They transformed my home. Attention to detail is unmatched.'
}, {
  name: 'David Chen',
  role: 'Office Manager • Paterson',
  img: 'https://i.pravatar.cc/150?img=11',
  quote: 'Professional, punctual, and meticulous. Highly recommended!'
}];
export function HomePage() {
  return <>
      <Hero data-id="element-493" />
      <Stats data-id="element-494" />

      {/* About Preview */}
      <section className="py-24 md:py-32 bg-brand-light" data-id="element-495">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center" data-id="element-496">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="grid grid-cols-2 gap-4" data-id="element-497">
            <img src={CLEANING_IMAGES[1]} alt="Clean space" className="rounded-3xl aspect-[3/4] object-cover shadow-xl" data-id="element-498" />
            <img src={CLEANING_IMAGES[6]} alt="Clean space" className="rounded-3xl aspect-[3/4] object-cover shadow-xl mt-8" data-id="element-499" />
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-500">
            <span className="text-brand-greenDark font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-501">
              ◆ About Us
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight text-brand-ink" data-id="element-502">
              15+ years of{' '}
              <span className="italic font-serif text-brand-navy" data-id="element-503">
                trusted excellence.
              </span>
            </h2>
            <p className="text-lg text-slate-700 mb-8 leading-relaxed" data-id="element-504">
              Based in Clifton, NJ, we deliver meticulous results for homes and
              businesses. As a proud member of MacAnthony Groups, we bring
              decades of reliability to every job — backed by eco-friendly
              products and a 100% satisfaction guarantee.
            </p>
            <Link to="/about" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-navy text-white font-bold text-lg hover:bg-brand-navyDark transition-all hover:shadow-glow hover:-translate-y-1" data-id="element-505">
              Learn About Us
              <ArrowRight className="w-5 h-5" data-id="element-506" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 md:py-32 bg-white" data-id="element-507">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-508">
          <div className="text-center max-w-2xl mx-auto mb-16" data-id="element-509">
            <span className="text-brand-greenDark font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-510">
              ◆ What We Do
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-brand-ink mb-6" data-id="element-511">
              Services that{' '}
              <span className="italic font-serif text-brand-navy" data-id="element-512">
                sparkle.
              </span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-513">
            {previewServices.map((service, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i % 3 * 0.1
          }} whileHover={{
            y: -6
          }} className="group p-7 bg-brand-light rounded-3xl border border-transparent hover:border-brand-navy hover:bg-white hover:shadow-soft transition-all" data-id="element-514">
                <div className="w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-5 group-hover:bg-brand-navy transition-colors" data-id="element-515">
                  <service.icon className="w-7 h-7 text-brand-navy group-hover:text-white transition-colors" data-id="element-516" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-brand-ink" data-id="element-517">
                  {service.title}
                </h3>
                <p className="text-slate-700 text-sm" data-id="element-518">{service.desc}</p>
              </motion.div>)}
          </div>

          <div className="text-center mt-12" data-id="element-519">
            <Link to="/services" className="inline-flex items-center gap-2 text-brand-navy font-bold text-lg hover:gap-3 transition-all" data-id="element-520">
              View All Services
              <ArrowRight className="w-5 h-5" data-id="element-521" />
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery Teaser */}
      <section className="py-24 bg-brand-light" data-id="element-522">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-523">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 gap-6" data-id="element-524">
            <div data-id="element-525">
              <span className="text-brand-greenDark font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-526">
                ◆ Our Work
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-brand-ink" data-id="element-527">
                See the sparkle.
              </h2>
            </div>
            <Link to="/gallery" className="inline-flex items-center gap-2 text-brand-navy font-bold hover:gap-3 transition-all" data-id="element-528">
              View Full Gallery <ArrowRight className="w-5 h-5" data-id="element-529" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-530">
            {CLEANING_IMAGES.slice(0, 4).map((img, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.08
          }} className="aspect-square overflow-hidden rounded-2xl group" data-id="element-531">
                <img src={img} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" data-id="element-532" />
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Testimonials Teaser */}
      <section className="py-24 bg-white" data-id="element-533">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-534">
          <div className="text-center max-w-2xl mx-auto mb-12" data-id="element-535">
            <span className="text-brand-greenDark font-bold tracking-widest uppercase text-sm mb-4 block" data-id="element-536">
              ◆ Client Love
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-brand-ink" data-id="element-537">
              What our clients{' '}
              <span className="italic font-serif text-brand-navy" data-id="element-538">say.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-539">
            {homeTestimonials.map((t, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="bg-brand-light rounded-3xl p-8 relative border border-slate-100" data-id="element-540">
                <Quote className="absolute top-6 right-8 w-12 h-12 text-brand-navy/5" data-id="element-541" />
                <div className="flex text-brand-gold mb-4" data-id="element-542">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-543" />)}
                </div>
                <p className="text-slate-800 mb-6 italic" data-id="element-544">"{t.quote}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-slate-200" data-id="element-545">
                  <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover" data-id="element-546" />
                  <div data-id="element-547">
                    <p className="font-bold text-brand-ink text-sm" data-id="element-548">{t.name}</p>
                    <p className="text-xs text-slate-600" data-id="element-549">{t.role}</p>
                  </div>
                </div>
              </motion.div>)}
          </div>
          <div className="text-center mt-10" data-id="element-550">
            <Link to="/reviews" className="inline-flex items-center gap-2 text-brand-navy font-bold hover:gap-3 transition-all" data-id="element-551">
              Read All Reviews <ArrowRight className="w-5 h-5" data-id="element-552" />
            </Link>
          </div>
        </div>
      </section>

      <CtaBanner data-id="element-553" />
    </>;
}